import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Autocomplete,
  TextField,
  ToggleButtonGroup,
  ToggleButton,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Stepper,
  Step,
  StepButton,
  Card,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  getEligibleSourceUsers,
  getEligibleRecipientUsers,
  getMoveWorkSourceOverview,
  getMoveWorkBuckets,
  getMoveWorkPendingApprovals,
  executeMoveWorkFlow,
} from "../../Services/WorkTransferApis";
import BucketLeadGroups, { BUCKET_LABELS, distinctNamesLabel } from "../../Components/WorkTransfer/BucketLeadGroups";
import InsightTiles from "../../Components/WorkTransfer/InsightTiles";
import { dormancyDays, dormancyTier, isNeverActive } from "../../Components/WorkTransfer/dormancy";
import WorkTransferNotice from "../../Components/WorkTransfer/WorkTransferNotice";
import { COLORS, PageHeader, Field, FieldLabel, PrimaryButton, SecondaryButton, fieldSx, cardSx, CircleCheckbox } from "../../Components/WorkTransfer/wtStyle";

// Phase 2 — "Move someone's work" (FR-1.1), Sales-mode-only build. A Sales
// user owns leads (Leads.userId), bucketed by pipeline stage. CRM mode
// (a CRM user's pending CRM.requestPendingWith approvals) lives in the full
// (non-sales-only) branch instead.

// Four-step wizard (2026-08-19 — was 3 steps with Review as a separate modal;
// Approval Transfer is now its own step instead of sharing "What & who to"
// with buckets, and Recipients + Review are merged into one inline step
// instead of a ReviewDialog popup): pick who -> pick what's eligible ->
// approval carry-forward -> recipients + review + confirm. ("Self + team"
// scope was removed entirely 2026-08-26 — this is just "who" now.)
const STEPS = ["Who's work is moving", "What's moving", "Approval transfer", "Recipients & review"];

// Lead type scopes the transfer the same way a bucket tick does — a lead of
// a non-matching type is excluded from this run entirely (2026-08-24). "All"
// is the default so the filter is opt-in, not a silent restriction.
const LEAD_TYPE_OPTIONS = ["All", "CP", "Direct", "R&L"];

// Same lead search used on "What's moving" (BucketLeadGroups.jsx), reused
// here for the Pending approvals step — display-only, never touches which
// approvals are selected, same principle as the bucket version.
function matchesLeadSearch(item, query) {
  if (!query) return true;
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return (item.leadname || "").toLowerCase().includes(q) || (item.leaddisplayid || "").toLowerCase().includes(q);
}

// Same name can belong to genuinely different accounts (test data, rehires) —
// disambiguate with email, and flag disabled accounts since those are exactly
// who this screen is for handing off (see getUsersWithOwnLeads, backend).
const sourceUserLabel = (u) => (u ? `${u.name || ""}${u.isActive === false ? " (Inactive)" : ""}` : "");

// "Reports to: X (Role)" — u.parentId is the embedded {name, roleId:{title}}
// from getUsersWithOwnLeads/getEligibleRecipientUsers, not a raw uuid here.
const reportsToLabel = (u) => {
  if (!u?.parentId) return null;
  const role = u.parentId.roleId?.title;
  return `Reports to: ${u.parentId.name}${role ? ` (${role})` : ""}`;
};

export default function MoveWorkPage({ canExecute = false }) {
  const [activeStep, setActiveStep] = useState(0);

  const [sourceUsers, setSourceUsers] = useState([]);
  const [sourceUser, setSourceUser] = useState(null);
  const [zeroLeadsPendingApprovalsCount, setZeroLeadsPendingApprovalsCount] = useState(0);
  // "Self + team" scope removed entirely (2026-08-26, per explicit request)
  // — Move Someone's Work now only ever operates on the selected user's own
  // leads/approvals, never a manager's direct reports too.
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const [loadingBuckets, setLoadingBuckets] = useState(false);
  const [buckets, setBuckets] = useState(null);
  const [unbucketedCount, setUnbucketedCount] = useState(0);
  const [selectedBuckets, setSelectedBuckets] = useState([]);
  const [excludedLeadIds, setExcludedLeadIds] = useState([]);
  const [leadSearchQuery, setLeadSearchQuery] = useState("");
  const [leadTypeFilter, setLeadTypeFilter] = useState("All");

  // Allotment bucket's own opt-in toggles (2026-08-25) — both default to
  // excluded (current safe behavior); ticking one re-fetches buckets with
  // that category folded back in as movable. Payment-conflicted leads have
  // no toggle at all — that exclusion always stays hard-blocked.
  const [includeCrmTagged, setIncludeCrmTagged] = useState(false);
  const [includeAgreementDone, setIncludeAgreementDone] = useState(false);

  const [recipientUsers, setRecipientUsers] = useState([]);
  const [recipients, setRecipients] = useState([]);
  const [distribution, setDistribution] = useState("single");
  const [reason, setReason] = useState("");

  // FR-7-equivalent for the three approval kinds. Fetched at Load, ticked
  // directly on their own step; anything left unticked stays with the
  // current owner.
  const [pendingApprovals, setPendingApprovals] = useState({ cancelRequests: [], changeUnitRequests: [], siteVisitApprovals: [] });
  // Which approval KINDS even show up as a section on the approval-transfer
  // step (2026-09-01) — a coarser, step-0-level choice on top of the
  // existing per-item carryForward*Ids below (which item within a shown
  // kind actually gets carried forward). Matches selectedBuckets' own
  // two-level pattern: pick the categories first, then the specifics.
  const [selectedApprovalKinds, setSelectedApprovalKinds] = useState([]);
  const [carryForwardCancelIds, setCarryForwardCancelIds] = useState([]);
  const [carryForwardChangeUnitIds, setCarryForwardChangeUnitIds] = useState([]);
  const [carryForwardSiteVisitApprovalIds, setCarryForwardSiteVisitApprovalIds] = useState([]);

  const [confirming, setConfirming] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [notice, setNotice] = useState(null);

  useEffect(() => {
    getEligibleSourceUsers()
      .then((res) => res.success && setSourceUsers(res.data.users))
      .catch(() => setNotice({ type: "error", title: "Couldn't load users", message: "Try reloading the page." }));
    getEligibleRecipientUsers()
      .then((res) => res.success && setRecipientUsers(res.data.users))
      .catch(() => setNotice({ type: "error", title: "Couldn't load users", message: "Try reloading the page." }));
    // Best-effort — an overview tile failing to load isn't worth an error
    // notice on page load, the rest of the page still works without it.
    getMoveWorkSourceOverview()
      .then((res) => res.success && setZeroLeadsPendingApprovalsCount(res.data.zeroLeadsPendingApprovalsCount))
      .catch(() => {});
  }, []);

  // Super Admin is flagged isCrmUser too, which would otherwise hide it here
  // even though it can directly own leads like a Sales user — let it through
  // specifically for testing.
  const filteredSourceUsers = sourceUsers.filter((u) => u.isCrmUser !== true || u.roleId?.title === "Super Admin");

  // Step 1 overview tiles — CP Sourcing's "All My CPs / Outreach / Escalate /
  // Critical" stat row, ported to the source-user picker. totalWithLeads/
  // inactiveWithLeads are free (already-loaded sourceUsers); zeroLeadsPending
  // needs its own query since those users own nothing and never appear in
  // sourceUsers at all (see getUsersWithZeroLeadsButPendingApprovals,
  // backend). No "transfer already in progress" tile — transfers here execute
  // atomically, there's no in-progress state to report.
  const sourceOverviewTiles = [
    { count: filteredSourceUsers.length, label: "Sales users with leads", hint: "assigned to them right now", tone: "blue" },
    { count: filteredSourceUsers.filter((u) => u.isActive === false).length, label: "Inactive, still holding leads", hint: "blocked accounts — hand these off", tone: "red" },
    { count: zeroLeadsPendingApprovalsCount, label: "Zero leads, pending approvals", hint: "role-based approver only", tone: "orange" },
  ];

  // Excluded-lead-aware count — a bucket's own .count is pre-exclusion (every
  // eligible lead in it), so ticked-off individual leads (excludedLeadIds)
  // must be subtracted here rather than trusted from bucket.count directly.
  // Moved above changeDistribution/toggleRecipient (2026-09-01) so the
  // round-robin recipient cap below can reference totalSelected.
  const excludedSet = new Set(excludedLeadIds);
  // Lead type (CP/Direct/R&L) scopes the transfer the same way a bucket tick
  // does (2026-08-24) — a lead of a non-matching type is never eligible for
  // this run, not just hidden from view, so it's folded into the same
  // exclusion accounting as a manually-unticked lead rather than kept as a
  // separate display-only filter.
  const matchesLeadType = (lead) => leadTypeFilter === "All" || lead.leadtype === leadTypeFilter;
  const selectedLeadsFlat = selectedBuckets.flatMap((name) => buckets?.[name]?.leads || []).filter((l) => !excludedSet.has(l.id) && matchesLeadType(l));
  const totalSelected = selectedLeadsFlat.length;

  const changeDistribution = (val) => {
    if (!val || val === distribution) return;
    setDistribution(val);
    setRecipients([]);
  };

  // Round robin splits totalSelected leads evenly across whoever's ticked,
  // in order — ticking more recipients than there are leads just means some
  // get 0 (confusing, and pointless). Capped at totalSelected once it's
  // known (2026-09-01); uncapped while totalSelected is 0 (e.g. an
  // approval-only transfer, where round robin isn't meaningfully bounded by
  // lead count at all).
  const toggleRecipient = (u) => {
    if (distribution === "single") {
      setRecipients((prev) => (prev[0]?.userId === u.userId ? [] : [u]));
      return;
    }
    setRecipients((prev) => {
      const isSelected = prev.some((r) => r.userId === u.userId);
      if (!isSelected && totalSelected > 0 && prev.length >= totalSelected) return prev;
      return isSelected ? prev.filter((r) => r.userId !== u.userId) : [...prev, u];
    });
  };

  // Clears everything loaded for whichever person was previously picked —
  // buckets, pending approvals, bucket/lead selections, recipients, reason.
  // Called whenever the source user changes (including clearing it entirely)
  // so stale data from the last person can't linger on screen after they're
  // removed; also reused after a successful transfer, which needs the exact
  // same clean slate before returning to step 0.
  const resetLoadedWork = () => {
    setBuckets(null);
    setUnbucketedCount(0);
    setSelectedBuckets([]);
    setExcludedLeadIds([]);
    setPendingApprovals({ cancelRequests: [], changeUnitRequests: [], siteVisitApprovals: [] });
    setSelectedApprovalKinds([]);
    setCarryForwardCancelIds([]);
    setCarryForwardChangeUnitIds([]);
    setCarryForwardSiteVisitApprovalIds([]);
    setRecipients([]);
    setReason("");
  };

  // overrides lets the Allotment toggles re-fetch immediately with the
  // JUST-CHANGED value, instead of the stale state a same-tick setState
  // wouldn't have applied yet (2026-08-25) — the plain "Load" button still
  // just uses whatever's currently in state.
  const loadBuckets = async (overrides = {}) => {
    if (!sourceUser) {
      setNotice({ type: "warning", title: "Pick a user first", message: "Select the user whose work you want to move." });
      return;
    }
    setLoadingBuckets(true);
    setBuckets(null);
    setUnbucketedCount(0);
    setSelectedBuckets([]);
    setExcludedLeadIds([]);
    try {
      const res = await getMoveWorkBuckets({
        userId: sourceUser.userId,
        dateFrom,
        dateTo,
        includeCrmTagged: overrides.includeCrmTagged ?? includeCrmTagged,
        includeAgreementDone: overrides.includeAgreementDone ?? includeAgreementDone,
      });
      if (!res.success) {
        // QUERY_TIMEOUT already retried once server-side — if it's still
        // timing out, this reads as a transient DB blip rather than a real
        // failure, so point the admin at the same Load button instead of
        // showing the raw Postgres error text.
        if (res.error === "QUERY_TIMEOUT") {
          setNotice({ type: "warning", title: "Taking longer than expected", message: "This didn't load in time — likely a brief slowdown. Click Load to try again." });
        } else {
          setNotice({ type: "error", title: "Couldn't load buckets", message: res.message });
        }
        return;
      }
      setBuckets(res.data.buckets);
      setUnbucketedCount(res.data.unbucketedCount || 0);
      // Nothing pre-ticked (2026-09-01) — an admin picks exactly which
      // buckets/approval kinds to move via the tiles below (or "Select all"),
      // rather than starting from everything and having to opt back out.
      setSelectedBuckets([]);
      setSelectedApprovalKinds([]);

      // Fetched here too (not just right before Review) so someone who owns
      // zero eligible leads but still has a pending approval to reassign
      // (Shreyas Kulkarni case) isn't blocked by "select a bucket" below —
      // we need to know upfront whether there's anything to carry forward.
      const pendingRes = await getMoveWorkPendingApprovals({ userId: sourceUser.userId });
      if (pendingRes.success) setPendingApprovals(pendingRes.data);
    } catch (err) {
      setNotice({ type: "error", title: "Couldn't load buckets", message: err.message });
    } finally {
      setLoadingBuckets(false);
    }
  };

  const toggleBucket = (name) => {
    const isRemoving = selectedBuckets.includes(name);
    setSelectedBuckets((prev) => (isRemoving ? prev.filter((n) => n !== name) : [...prev, name]));
    // Turning a bucket off entirely clears its per-lead exclusions too, so
    // re-ticking it later starts fresh with everyone included rather than
    // silently remembering a stale partial selection.
    if (isRemoving) {
      const bucketLeadIds = new Set((buckets?.[name]?.leads || []).map((l) => l.id));
      setExcludedLeadIds((prev) => prev.filter((id) => !bucketLeadIds.has(id)));
    }
  };

  // Individual lead checkbox — lets an admin leave one lead behind while the
  // rest of its bucket still moves (2026-08-19). Also doubles as "pick just
  // this one lead": ticking a lead in a bucket that isn't selected yet
  // selects the bucket but excludes every other lead in it, instead of
  // forcing the admin through "select all, then untick everyone else".
  const toggleLeadExclusion = (bucketName, leadId) => {
    if (!selectedBuckets.includes(bucketName)) {
      const bucketLeadIds = (buckets?.[bucketName]?.leads || []).map((l) => l.id);
      setSelectedBuckets((prev) => [...prev, bucketName]);
      setExcludedLeadIds((prev) => [...prev.filter((id) => !bucketLeadIds.includes(id)), ...bucketLeadIds.filter((id) => id !== leadId)]);
      return;
    }
    setExcludedLeadIds((prev) => (prev.includes(leadId) ? prev.filter((id) => id !== leadId) : [...prev, leadId]));
  };

  // Bucket header checkbox, going from unchecked/indeterminate to fully
  // checked — clears exclusions for every lead in that bucket so "select all"
  // actually means all, not "whatever was left over from before".
  const selectAllInBucket = (bucketName) => {
    const bucketLeadIds = new Set((buckets?.[bucketName]?.leads || []).map((l) => l.id));
    setExcludedLeadIds((prev) => prev.filter((id) => !bucketLeadIds.has(id)));
  };

  const hasPendingApprovals =
    pendingApprovals.cancelRequests.length > 0 ||
    pendingApprovals.changeUnitRequests.length > 0 ||
    pendingApprovals.siteVisitApprovals.length > 0;

  const toggleApprovalKind = (key) => {
    setSelectedApprovalKinds((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  };

  // Nothing is pre-ticked (2026-09-01) — block Next if there's genuinely
  // nothing selected yet, same wording/pattern as goToApprovalStep below.
  const goToBucketStep = () => {
    if (selectedBuckets.length === 0 && selectedApprovalKinds.length === 0) {
      setNotice({ type: "warning", title: "Select at least one bucket or approval type", message: "Tap a tile above to choose what to move." });
      return;
    }
    setActiveStep(1);
  };

  // Someone can own zero eligible leads and still have a pending approval to
  // reassign (role-based approver, not lead owner) — only block "no bucket
  // selected" if no approval kind was picked either.
  const goToApprovalStep = () => {
    if (selectedBuckets.length === 0 && selectedApprovalKinds.length === 0) {
      setNotice({ type: "warning", title: "Select at least one bucket", message: "Tick which buckets to move." });
      return;
    }
    setActiveStep(2);
  };

  const toggleCarryForwardCancel = (id) => {
    setCarryForwardCancelIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };
  const toggleCarryForwardChangeUnit = (id) => {
    setCarryForwardChangeUnitIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };
  const toggleCarryForwardSiteVisitApproval = (leadId) => {
    setCarryForwardSiteVisitApprovalIds((prev) => (prev.includes(leadId) ? prev.filter((x) => x !== leadId) : [...prev, leadId]));
  };

  // Three approval sections rendered, each with its own count and checkboxes,
  // leads shown inline (2026-08-24 — dropped the separate "View leads" side
  // panel toggle to match how BucketLeadGroups already shows step 2's leads).
  // Site Visit approval is APPROVER-based (Leads.userParentId) — completely
  // separate from the
  // OWNER-based Site Visit bucket (Verified/Unverified/Rejected, on the
  // previous step, Leads.userId) — and only ever shows still-pending items,
  // same rule as Cancellation/Unit-Shift: once decided, it's no longer
  // "pending with" anyone, so it drops out.
  const APPROVAL_KINDS = [
    {
      key: "CancellationApproval",
      label: "Cancellation approval",
      items: pendingApprovals.cancelRequests,
      idField: "cancelUnitRequestId",
      selectedIds: carryForwardCancelIds,
      onToggle: toggleCarryForwardCancel,
      isSelectable: () => true,
    },
    {
      key: "UnitShiftApproval",
      label: "Unit shift approval",
      items: pendingApprovals.changeUnitRequests,
      idField: "changeUnitRequestId",
      selectedIds: carryForwardChangeUnitIds,
      onToggle: toggleCarryForwardChangeUnit,
      isSelectable: () => true,
    },
    {
      key: "SiteVisitApproval",
      label: "Site visit approval pending with you",
      items: pendingApprovals.siteVisitApprovals,
      idField: "leadId",
      selectedIds: carryForwardSiteVisitApprovalIds,
      onToggle: toggleCarryForwardSiteVisitApproval,
      isSelectable: () => true,
    },
  ];

  const itemWord = "lead";

  // Step 2 dormancy tiles — computed over every loaded lead regardless of
  // which buckets are ticked (same "overview of everything eligible" framing
  // CP Sourcing's stat row uses), from last_updated_at already present on
  // every leadsfilter row. Total eligible / excluded / unbucketed are free —
  // the backend already computes those; only the dormancy tiering itself is
  // new, and it's plain client-side arithmetic, no extra query.
  const bucketOverviewTiles = (() => {
    if (!buckets) return [];
    const tierCounts = { outreach: 0, escalate: 0, critical: 0 };
    let neverActive = 0;
    let totalEligible = 0;
    let totalExcluded = 0;
    // Scoped to only the buckets ticked at step 0 (2026-09-01) — "What's
    // eligible" should describe what's actually being moved, not everything
    // that theoretically could be.
    for (const [name, bucket] of Object.entries(buckets).filter(([n]) => selectedBuckets.includes(n))) {
      totalEligible += bucket.count;
      totalExcluded += bucket.excludedCount || 0;
      for (const lead of bucket.leads) {
        const tier = dormancyTier(dormancyDays(lead.last_updated_at));
        if (tier) tierCounts[tier]++;
        if (isNeverActive(lead, name)) neverActive++;
      }
    }
    return [
      { count: totalEligible, label: "Total eligible leads", tone: "blue" },
      { count: tierCounts.outreach, label: "Outreach — 14–29d quiet", tone: "grey" },
      { count: tierCounts.escalate, label: "Escalate — 30–59d quiet", tone: "orange" },
      { count: tierCounts.critical, label: "Critical — 60d+ quiet", tone: "red" },
      { count: neverActive, label: "Never active", hint: "no site visit, ever", tone: "grey" },
      { count: totalExcluded, label: "Excluded from transfer", hint: "payment/CRM conflict", tone: "red" },
      { count: unbucketedCount, label: "Unbucketed", hint: "status didn't match a known bucket", tone: "grey" },
    ];
  })();

  // Mirrors distribution.js's buildLeadOperations exactly — usableRecipients[i
  // % usableRecipients.length] over the SAME ordered concatenation of selected
  // buckets' leads (bucketNames in this same array order is what gets sent to
  // the backend) — so this preview always matches what actually gets applied.
  const recipientCounts = (() => {
    if (distribution !== "roundrobin" || recipients.length === 0) return [];
    const orderedLeads = selectedLeadsFlat;
    const counts = recipients.map(() => 0);
    orderedLeads.forEach((_, i) => counts[i % recipients.length]++);
    return recipients.map((r, i) => ({ ...r, count: counts[i] }));
  })();

  // Ticked approval counts (Cancellation/Unit Shift/Site Visit) — separate
  // from totalSelected (bucket leads only) since an admin can carry forward
  // approvals with zero buckets selected (Shreyas Kulkarni case). Without
  // this line the summary silently said nothing about approvals at all, even
  // when several were ticked on the previous step.
  const totalApprovalsCarriedForward = carryForwardCancelIds.length + carryForwardChangeUnitIds.length + carryForwardSiteVisitApprovalIds.length;

  // Left-behind count now folds in BOTH reasons a lead can be excluded —
  // manually unticked (excludedSet) and type-filter mismatch (matchesLeadType)
  // — previously only the manual case was counted, so switching Lead type to
  // e.g. "CP" silently dropped every Direct/R&L lead with no line explaining
  // where they went.
  const excludedInSelectedBucketsCount = selectedBuckets.reduce(
    (sum, name) => sum + (buckets?.[name]?.leads || []).filter((l) => excludedSet.has(l.id) || !matchesLeadType(l)).length,
    0
  );

  // Only list buckets that actually contribute an included lead — a bucket
  // stays in selectedBuckets by default from Load even if every one of its
  // leads got individually unticked or filtered out by type, which used to
  // make the summary claim buckets with zero real leads moving in them.
  // Per-bucket counts also surface at a glance which bucket(s) leads are
  // actually coming from when more than one is involved.
  const bucketsWithIncludedCounts = selectedBuckets
    .map((name) => ({
      name,
      count: (buckets?.[name]?.leads || []).filter((l) => !excludedSet.has(l.id) && matchesLeadType(l)).length,
    }))
    .filter((b) => b.count > 0);

  // Eligible-vs-moving and the payment/CRM-conflict count are both computed
  // over EVERY loaded bucket (not just selectedBuckets) — they're a reference
  // point against the whole pool the admin was shown, not just what ended up
  // ticked, mirroring how bucketOverviewTiles frames "what's eligible" back
  // in step 1.
  let totalEligibleAcrossAllBuckets = 0;
  let totalPaymentConflictExcluded = 0;
  const paymentConflictReasons = new Set();
  for (const bucket of Object.values(buckets || {})) {
    totalEligibleAcrossAllBuckets += bucket.count;
    totalPaymentConflictExcluded += bucket.excludedCount || 0;
    if (bucket.excludedReason) paymentConflictReasons.add(bucket.excludedReason);
  }

  const formatDateInput = (isoDate) =>
    isoDate ? new Date(`${isoDate}T00:00:00`).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : null;

  const summaryLines = [
    totalSelected === 0 && totalApprovalsCarriedForward > 0
      ? `Carrying forward ${totalApprovalsCarriedForward} pending approval${totalApprovalsCarriedForward === 1 ? "" : "s"} for ${sourceUser?.name} — no leads selected.`
      : `Moving ${totalSelected} of ${totalEligibleAcrossAllBuckets} eligible ${itemWord}${totalEligibleAcrossAllBuckets === 1 ? "" : "s"} from ${sourceUser?.name}.`,
    ...(bucketsWithIncludedCounts.length > 0
      ? [`Buckets: ${bucketsWithIncludedCounts.map((b) => `${b.name} (${b.count})`).join(", ")}.`]
      : []),
    ...(leadTypeFilter !== "All" ? [`Lead type: ${leadTypeFilter} only.`] : []),
    ...(excludedInSelectedBucketsCount > 0
      ? [`${excludedInSelectedBucketsCount} lead${excludedInSelectedBucketsCount === 1 ? "" : "s"} left behind (individually unticked or a different lead type).`]
      : []),
    // Spells out WHY, using the backend's own excludedReason text (bucketComputation.js)
    // instead of the bare "(payment/CRM conflict)" hint — these are leads the
    // backend hard-excluded before they ever became selectable (Allotment leads
    // with a completed CP payment entry and/or a CRM record already against
    // them), distinct from "left behind" leads above, which the admin chose
    // not to move and could have moved if they wanted to.
    ...(totalPaymentConflictExcluded > 0
      ? [
          `${totalPaymentConflictExcluded} lead${totalPaymentConflictExcluded === 1 ? "" : "s"} excluded from transfer — never selectable, not a manual choice: ${[...paymentConflictReasons].join(" ")}`,
        ]
      : []),
    ...(dateFrom || dateTo ? [`Date range: ${formatDateInput(dateFrom) || "any"} – ${formatDateInput(dateTo) || "any"}.`] : []),
    ...(totalApprovalsCarriedForward > 0
      ? [
          `Approvals carried forward: ${carryForwardCancelIds.length} cancellation, ${carryForwardChangeUnitIds.length} unit shift, ${carryForwardSiteVisitApprovalIds.length} site visit.`,
        ]
      : []),
    distribution === "roundrobin"
      ? `To (round robin): ${recipientCounts.map((r) => `${r.name} (${r.count})`).join(", ")}.`
      : `To: ${recipients[0]?.name}${totalSelected > 0 ? ` (all ${totalSelected})` : ""}.`,
  ];

  // Recipients + round-robin-minimum + reason are validated here (inline,
  // via disabling Confirm) instead of the old separate "Review transfer" gate
  // — there's no longer a distinct dialog to gate entry into, Confirm IS the
  // gate now.
  const confirmDisabled =
    confirming ||
    !canExecute ||
    recipients.length === 0 ||
    (distribution === "roundrobin" && recipients.length < 2) ||
    reason.trim().length === 0;

  const confirmTransfer = async () => {
    setConfirming(true);
    try {
      const performedBy = JSON.parse(localStorage.getItem("user"))?.userId;
      // The backend only knows about excludedLeadIds — it has no notion of
      // leadTypeFilter — so a type-mismatched lead in a selected bucket must
      // be folded into the same exclusion list sent over the wire, or it'd
      // get transferred despite not matching the type picked on-screen.
      const typeMismatchIds = selectedBuckets.flatMap((name) => (buckets?.[name]?.leads || []).filter((l) => !matchesLeadType(l)).map((l) => l.id));
      const effectiveExcludedLeadIds = Array.from(new Set([...excludedLeadIds, ...typeMismatchIds]));
      const res = await executeMoveWorkFlow({
        userId: sourceUser.userId,
        dateFrom,
        dateTo,
        bucketNames: selectedBuckets,
        excludedLeadIds: effectiveExcludedLeadIds,
        recipients: recipients.map((r) => r.userId),
        distribution,
        reason,
        performedBy,
        cancelRequestIds: carryForwardCancelIds,
        changeUnitRequestIds: carryForwardChangeUnitIds,
        siteVisitApprovalIds: carryForwardSiteVisitApprovalIds,
      });
      if (!res.success) {
        setNotice({ type: "error", title: "Transfer failed", message: res.message });
        return;
      }
      setNotice({ type: "success", title: "Transfer confirmed", message: `${res.data.operationsApplied} ${itemWord}(s) moved.` });
      resetLoadedWork();
      setActiveStep(0);
    } catch (err) {
      setNotice({ type: "error", title: "Transfer failed", message: err.message });
    } finally {
      setConfirming(false);
    }
  };

  return (
    <Box sx={{ p: 3, display: "flex", gap: 3, alignItems: "flex-start" }}>
      <Box sx={{ maxWidth: "none", width: "100%", flexShrink: 0 }}>
        {/* Frozen while the bucket list below scrolls (2026-08-24) — title,
            stepper, search, and "moving work for" stay in view the whole
            time instead of scrolling away once a bucket's lead list grows
            past a screen's height. */}
        <Box sx={{ position: "sticky", top: 0, zIndex: 5, bgcolor: "#fff", pb: 1 }}>
        <PageHeader
          title="Move someone's work"
          subtitle="Search a person to see everything they currently own."
        />

        {/* Stepper fills 100% of its parent by default — fine at the old
            fixed 800px width, but spread out into unreadably long connector
            lines once the page went full-width. Capped here, independent of
            how wide the rest of the page's content gets. Global search sits
            in the gutter this leaves on the right (2026-08-24) — it works on
            both "What's moving" and "Approval transfer" since both list
            leads by name; not mounted (and empty-looking) on any other step.
            Lead type moved to step 0 (2026-09-01, was here, step-1-only) —
            it still scopes the same matchesLeadType() everywhere downstream,
            just picked earlier alongside the buckets it scopes. */}
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, mb: 3 }}>
          <Box sx={{ maxWidth: 960, flex: 1 }}>
            <Stepper
              activeStep={activeStep}
              sx={{
                "& .MuiStepLabel-label": { fontSize: 13.5, fontWeight: 600 },
                "& .MuiStepLabel-label.Mui-active": { fontWeight: 700 },
                "& .MuiStepIcon-root": { fontSize: 26 },
              }}
            >
              {STEPS.map((label, i) => (
                <Step key={label} completed={i < activeStep}>
                  <StepButton onClick={() => setActiveStep(i)} disabled={i > 0 && !buckets}>
                    {label}
                  </StepButton>
                </Step>
              ))}
            </Stepper>
          </Box>
          {(activeStep === 1 || activeStep === 2) && (
            <Field label="Search leads" sx={{ width: 340, flexShrink: 0 }}>
              <TextField
                placeholder="Lead CUID or lead name"
                value={leadSearchQuery}
                onChange={(e) => setLeadSearchQuery(e.target.value)}
                sx={fieldSx}
              />
            </Field>
          )}
        </Box>
        {(activeStep === 1 || activeStep === 2) && (totalSelected > 0 || (activeStep === 2 && totalApprovalsCarriedForward > 0)) && (
          <Box sx={{ mb: 2, mt: -1.5, display: "flex", gap: 1 }}>
            {totalSelected > 0 && (
              <Chip
                label={`${totalSelected} lead${totalSelected === 1 ? "" : "s"} selected so far`}
                size="small"
                sx={{ fontSize: 12.5, fontWeight: 600, bgcolor: COLORS.blueLight, color: COLORS.blue }}
              />
            )}
            {/* Step 3 (Approval transfer) ticks approvals, not leads — the
                lead chip above carries over from step 2 but says nothing
                about what's actually being selected on THIS step, so an
                admin scrolling a long approvals list had no persistent
                feedback on how many they'd ticked so far (2026-08-25). */}
            {activeStep === 2 && totalApprovalsCarriedForward > 0 && (
              <Chip
                label={`${totalApprovalsCarriedForward} approval${totalApprovalsCarriedForward === 1 ? "" : "s"} selected so far`}
                size="small"
                sx={{ fontSize: 12.5, fontWeight: 600, bgcolor: "#fef3c7", color: COLORS.orange }}
              />
            )}
          </Box>
        )}

        {activeStep === 0 && sourceUser && (
          <Box sx={{ mb: 2 }}>
            <Typography fontSize={12} color={COLORS.textSecondary}>
              Currently moving work for <b>{sourceUser.name}</b>
              {sourceUser.roleId?.title ? ` — ${sourceUser.roleId.title}` : ""}
            </Typography>
          </Box>
        )}
        {activeStep > 0 && sourceUser && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <Typography fontSize={12} color={COLORS.textSecondary}>
              Moving work for <b>{sourceUser.name}</b>
              {sourceUser.roleId?.title ? ` — ${sourceUser.roleId.title}` : ""}.
            </Typography>
            <Typography
              fontSize={12}
              color={COLORS.blue}
              sx={{ cursor: "pointer", fontWeight: 600 }}
              onClick={() => setActiveStep(0)}
            >
              Change
            </Typography>
          </Box>
        )}
        </Box>

        {activeStep === 0 && (
        <Box sx={{ ...cardSx, p: 2, mb: 2 }}>
          <InsightTiles tiles={sourceOverviewTiles} />
          <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap", alignItems: "flex-end" }}>
            <Field label="Who's work is moving" sx={{ width: 300 }}>
              <Autocomplete
                options={filteredSourceUsers}
                getOptionLabel={sourceUserLabel}
                isOptionEqualToValue={(o, v) => o.userId === v.userId}
                renderOption={(props, u) => (
                  <Box component="li" {...props} key={u.userId} sx={{ display: "block !important" }}>
                    <Typography fontSize={13.5} fontWeight={600}>
                      {u.name}
                      {u.roleId?.title && (
                        <Typography component="span" fontSize={11} color={COLORS.blue} sx={{ ml: 0.75, fontWeight: 600 }}>
                          {u.roleId.title}
                        </Typography>
                      )}
                      {u.isActive === false && (
                        <Typography component="span" fontSize={11} color={COLORS.orange} sx={{ ml: 0.75, fontWeight: 600 }}>
                          INACTIVE
                        </Typography>
                      )}
                    </Typography>
                    <Typography fontSize={12} color={COLORS.textSecondary}>
                      {u.email}
                      {reportsToLabel(u) ? ` · ${reportsToLabel(u)}` : ""}
                    </Typography>
                  </Box>
                )}
                value={sourceUser}
                onChange={(e, val) => {
                  setSourceUser(val);
                  resetLoadedWork();
                }}
                renderInput={(params) => <TextField {...params} placeholder="Choose a user" />}
                sx={fieldSx}
              />
            </Field>
            {/* Lead type/From/To/Load share the picker's row (2026-09-01) —
                fills the wide gutter the picker used to leave empty on its
                own line, instead of stacking below it. */}
            {sourceUser && (
              <Field label="Lead type" sx={{ flexShrink: 0 }}>
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={leadTypeFilter}
                  onChange={(e, value) => value && setLeadTypeFilter(value)}
                  sx={{
                    "& .MuiToggleButton-root": { fontSize: 12.5, fontWeight: 500, textTransform: "none", borderColor: COLORS.border, px: 1.75 },
                    "& .Mui-selected": { bgcolor: `${COLORS.blueLight} !important`, color: `${COLORS.blue} !important` },
                  }}
                >
                  {LEAD_TYPE_OPTIONS.map((opt) => (
                    <ToggleButton key={opt} value={opt}>
                      {opt}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Field>
            )}
            <Field label="From">
              <TextField type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} sx={fieldSx} />
            </Field>
            <Field label="To">
              <TextField type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} sx={fieldSx} />
            </Field>
            <PrimaryButton onClick={loadBuckets} disabled={loadingBuckets}>
              {loadingBuckets ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Load"}
            </PrimaryButton>
          </Box>

          {sourceUser && (
            <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
              <Typography fontSize={12} color={COLORS.textSecondary}>
                <b>{sourceUser.name}</b>
                {sourceUser.roleId?.title ? ` — ${sourceUser.roleId.title}` : ""}
                {reportsToLabel(sourceUser) ? ` — ${reportsToLabel(sourceUser)}` : " — no manager on record"}
              </Typography>
            </Box>
          )}

          {/* Bucket-wise + approval-wise counts, right after Load — a quick
              preview of what's actually there before committing to Next,
              instead of only finding out once inside "What's moving". Same
              tile pattern as sourceOverviewTiles above, full-width, rather
              than a narrow sidebar with empty space next to it. */}
          {buckets && (() => {
            // Tile counts scoped by leadTypeFilter (2026-09-01) — bucket.count
            // is the raw server total regardless of type; picking "CP" here
            // previously left every number unchanged, which read as broken.
            const typeFilteredCounts = Object.fromEntries(
              Object.entries(buckets).map(([name, bucket]) => [name, bucket.leads.filter(matchesLeadType).length])
            );
            const selectableBucketNames = Object.entries(typeFilteredCounts).filter(([, c]) => c > 0).map(([name]) => name);
            const APPROVAL_KIND_COUNTS = [
              { key: "CancellationApproval", label: "Cancellation", count: pendingApprovals.cancelRequests.length },
              { key: "UnitShiftApproval", label: "Unit Shift", count: pendingApprovals.changeUnitRequests.length },
              { key: "SiteVisitApproval", label: "Site Visit", count: pendingApprovals.siteVisitApprovals.length },
            ];
            const selectableApprovalKinds = APPROVAL_KIND_COUNTS.filter((k) => k.count > 0).map((k) => k.key);
            return (
              <>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 1, mb: 1 }}>
                  <Typography fontSize={13} fontWeight={700}>
                    Buckets — tap to choose which ones to move
                  </Typography>
                  {selectableBucketNames.length > 0 && (
                    <Typography
                      fontSize={12.5}
                      fontWeight={600}
                      sx={{ color: COLORS.blue, cursor: "pointer" }}
                      onClick={() =>
                        setSelectedBuckets(selectedBuckets.length === selectableBucketNames.length ? [] : selectableBucketNames)
                      }
                    >
                      {selectedBuckets.length === selectableBucketNames.length ? "Clear all" : "Select all"}
                    </Typography>
                  )}
                </Box>
                <InsightTiles
                  tiles={Object.keys(buckets).map((name) => ({
                    count: typeFilteredCounts[name],
                    label: BUCKET_LABELS[name] || name,
                    tone: typeFilteredCounts[name] > 0 ? "blue" : "grey",
                    selected: selectedBuckets.includes(name),
                    onClick: () => toggleBucket(name),
                  }))}
                />
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                  <Typography fontSize={13} fontWeight={700}>
                    Pending approvals — tap to choose which types to carry forward
                  </Typography>
                  {selectableApprovalKinds.length > 0 && (
                    <Typography
                      fontSize={12.5}
                      fontWeight={600}
                      sx={{ color: COLORS.blue, cursor: "pointer" }}
                      onClick={() =>
                        setSelectedApprovalKinds(
                          selectedApprovalKinds.length === selectableApprovalKinds.length ? [] : selectableApprovalKinds
                        )
                      }
                    >
                      {selectedApprovalKinds.length === selectableApprovalKinds.length ? "Clear all" : "Select all"}
                    </Typography>
                  )}
                </Box>
                <InsightTiles
                  tiles={APPROVAL_KIND_COUNTS.map((k) => ({
                    count: k.count,
                    label: k.label,
                    tone: k.count > 0 ? "orange" : "grey",
                    selected: selectedApprovalKinds.includes(k.key),
                    onClick: () => toggleApprovalKind(k.key),
                  }))}
                />
              </>
            );
          })()}

          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2.5 }}>
            <PrimaryButton onClick={goToBucketStep} disabled={!buckets}>
              Next →
            </PrimaryButton>
          </Box>
        </Box>
        )}

        {activeStep === 1 && buckets && (
          <Box sx={{ ...cardSx, p: 2, mb: 2 }}>
            <Typography fontSize={13} fontWeight={700} sx={{ mb: 1.5 }}>
              What's eligible
            </Typography>
            <InsightTiles tiles={bucketOverviewTiles} />
            <BucketLeadGroups
              buckets={Object.fromEntries(Object.entries(buckets).filter(([name]) => selectedBuckets.includes(name)))}
              selected={selectedBuckets}
              onToggle={toggleBucket}
              excludedLeadIds={excludedLeadIds}
              onToggleLead={toggleLeadExclusion}
              onSelectAllInBucket={selectAllInBucket}
              searchQuery={leadSearchQuery}
              leadTypeFilter={leadTypeFilter}
              allotmentOptions={{
                includeCrmTagged,
                includeAgreementDone,
                onToggleCrmTagged: (checked) => {
                  setIncludeCrmTagged(checked);
                  loadBuckets({ includeCrmTagged: checked });
                },
                onToggleAgreementDone: (checked) => {
                  setIncludeAgreementDone(checked);
                  loadBuckets({ includeAgreementDone: checked });
                },
              }}
            />

            <Box sx={{ display: "flex", justifyContent: "space-between", mt: 2.5 }}>
              <SecondaryButton onClick={() => setActiveStep(0)}>← Back</SecondaryButton>
              <PrimaryButton onClick={goToApprovalStep}>Next →</PrimaryButton>
            </Box>
          </Box>
        )}

        {activeStep === 2 && (
          <Box sx={{ ...cardSx, p: 2, mb: 2 }}>
            <Typography fontSize={13} fontWeight={700} sx={{ mb: 1 }}>
              Pending approvals
            </Typography>
            {(() => {
              // Only the kinds ticked at step 0 ever render here (2026-09-01).
              const selectedKinds = APPROVAL_KINDS.filter((kind) => selectedApprovalKinds.includes(kind.key));
              if (selectedKinds.length === 0) {
                return (
                  <Typography fontSize={14} color={COLORS.textSecondary} sx={{ py: 2 }}>
                    No approval type was selected — nothing will be carried forward.
                  </Typography>
                );
              }
              const isSearching = leadSearchQuery.trim().length > 0;
              const anyMatch =
                !isSearching || selectedKinds.some((kind) => kind.items.some((r) => matchesLeadSearch(r, leadSearchQuery) && matchesLeadType(r)));
              if (!anyMatch) {
                return (
                  <Typography fontSize={14} color={COLORS.textSecondary} sx={{ py: 2 }}>
                    No leads match "{leadSearchQuery}" in any pending approval.
                  </Typography>
                );
              }
              return null;
            })()}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {APPROVAL_KINDS.filter((kind) => selectedApprovalKinds.includes(kind.key)).map((kind) => {
                const isSearching = leadSearchQuery.trim().length > 0;
                // Lead type scopes step 3 the same way it scopes step 2's
                // buckets (2026-08-24) — a non-matching-type approval is
                // hidden AND excluded from select-all/counts, not just
                // dimmed, so "CP only" genuinely means only CP leads' pending
                // approvals can be carried forward. Search still hides an
                // empty-match kind entirely; the type filter alone does not
                // (an admin should still see "0 of N" rather than the whole
                // section vanishing).
                const isFiltering = isSearching || leadTypeFilter !== "All";
                const visibleItems = kind.items.filter((r) => matchesLeadSearch(r, leadSearchQuery) && matchesLeadType(r));
                if (isSearching && visibleItems.length === 0) return null;
                const smLabel = distinctNamesLabel(kind.items, "salesname", "SM");
                // Approver-kind groups only — who the approval is CURRENTLY
                // pending with, distinct from SM (who owns the lead). These
                // two can differ even for a single selected user — approval
                // rows are matched by requestPendingWithUserId, not by lead
                // ownership, so a lead owned by someone else entirely can
                // still show up here (confirmed real case: a lead owned by
                // Super Admin, approval pending with Vedashree) — so this
                // header line can legitimately show more than one SM name
                // (2026-08-25).
                const approverLabel = distinctNamesLabel(kind.items, "approverName", "Approver");
                const selectableItems = kind.items.filter((r) => kind.isSelectable(r) && matchesLeadType(r));
                const allChecked = selectableItems.length > 0 && selectableItems.every((r) => kind.selectedIds.includes(r[kind.idField]));
                const someChecked = selectableItems.some((r) => kind.selectedIds.includes(r[kind.idField]));
                // Header checkbox selects/deselects every item in this
                // section at once — same "whole-group toggle" the bucket
                // cards use — while the per-lead checkboxes below still let
                // the admin fine-tune.
                const toggleAll = () => {
                  selectableItems.forEach((r) => {
                    const id = r[kind.idField];
                    const isSelected = kind.selectedIds.includes(id);
                    if (allChecked === isSelected) kind.onToggle(id);
                  });
                };
                return (
                  <Card
                    key={kind.key}
                    elevation={0}
                    sx={{
                      border: `1px solid ${someChecked ? COLORS.blue : COLORS.border}`,
                      borderRadius: 3,
                      bgcolor: someChecked ? COLORS.blueLight : "#fff",
                      overflow: "hidden",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", p: 1.5, cursor: "pointer" }} onClick={toggleAll}>
                      <CircleCheckbox
                        checked={allChecked}
                        indeterminate={someChecked && !allChecked}
                        onChange={toggleAll}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <Box sx={{ flex: 1 }}>
                        <Typography fontSize={13.5} fontWeight={600}>{kind.label}</Typography>
                      </Box>
                      {approverLabel && (
                        <Typography fontSize={12} color={COLORS.orange} sx={{ mr: 1.5, flexShrink: 0 }} noWrap>
                          {approverLabel}
                        </Typography>
                      )}
                      {smLabel && (
                        <Typography fontSize={12} color={COLORS.textSecondary} sx={{ mr: 1.5, flexShrink: 0 }} noWrap>
                          {smLabel}
                        </Typography>
                      )}
                      <Chip
                        label={isFiltering ? `${visibleItems.length} match${visibleItems.length === 1 ? "" : "es"}` : `${kind.items.length} lead${kind.items.length === 1 ? "" : "s"}`}
                        size="small"
                        sx={{ fontSize: 12, fontWeight: 500, bgcolor: COLORS.sunken, color: COLORS.textSecondary }}
                      />
                    </Box>
                    {visibleItems.length > 0 && (
                      <List dense disablePadding sx={{ px: 1.5, pb: 1 }}>
                        {visibleItems.map((r) => {
                          const isChecked = kind.selectedIds.includes(r[kind.idField]);
                          return (
                          <ListItem key={r[kind.idField]} disableGutters sx={{ bgcolor: isChecked ? COLORS.blueLight : "transparent", borderRadius: 1 }}>
                            {kind.isSelectable(r) ? (
                              <CircleCheckbox size="small" checked={isChecked} onChange={() => kind.onToggle(r[kind.idField])} />
                            ) : (
                              <Box sx={{ width: 42, flexShrink: 0 }} />
                            )}
                            {/* SM and full leadstatus text moved inline
                                (2026-08-26) — a fixed-width Chip truncated
                                the longer statuses ("Cancellation
                                Confirmation Pending by X" etc.) to an
                                unreadable "...", and the whole point of
                                showing status lead-by-lead is to read it, not
                                just see that something's there. Text wraps
                                naturally here instead. */}
                            <ListItemText
                              primaryTypographyProps={{ fontSize: 13.5 }}
                              secondaryTypographyProps={{ fontSize: 12 }}
                              primary={r.leadname || r.leaddisplayid || (r.leadId ? r.leadId.slice(0, 8) : "—")}
                              secondary={
                                <>
                                  {r.salesname && `SM: ${r.salesname} — `}
                                  {r.leaddisplayid || ""}
                                  {r.leadstatus && ` — ${r.leadstatus}`}
                                  {r.verificationStatus && ` — Site visit: ${r.verificationStatus}`}
                                  {r.approverName && ` — Approver: ${r.approverName}`}
                                </>
                              }
                            />
                          </ListItem>
                          );
                        })}
                      </List>
                    )}
                  </Card>
                );
              })}
            </Box>
            <Typography fontSize={12} color={COLORS.textSecondary} sx={{ display: "block", mt: 1 }}>
              Tick to move an approval to the recipient. Leave unticked to keep it with {sourceUser?.name}. Skippable — Next works even with nothing ticked here.
            </Typography>

            <Box sx={{ display: "flex", justifyContent: "space-between", mt: 2.5 }}>
              <SecondaryButton onClick={() => setActiveStep(1)}>← Back</SecondaryButton>
              <PrimaryButton onClick={() => setActiveStep(3)}>Next →</PrimaryButton>
            </Box>
          </Box>
        )}

        {activeStep === 3 && (
          <Box sx={{ ...cardSx, p: 2, mb: 2 }}>
            <Typography fontSize={13} fontWeight={700} sx={{ mb: 1 }}>
              Recipients
            </Typography>

            <Box sx={{ mb: 1.5 }}>
              <Field label="Distribution">
                <ToggleButtonGroup
                  value={distribution}
                  exclusive
                  onChange={(e, val) => changeDistribution(val)}
                  sx={{
                    "& .MuiToggleButton-root": { fontSize: 13.5, fontWeight: 500, textTransform: "none", borderColor: COLORS.border, py: 1.05, px: 2 },
                    "& .Mui-selected": { bgcolor: `${COLORS.blueLight} !important`, color: `${COLORS.blue} !important` },
                  }}
                >
                  <ToggleButton value="single">Single recipient</ToggleButton>
                  <ToggleButton value="roundrobin">Round robin (2+ users)</ToggleButton>
                </ToggleButtonGroup>
              </Field>
            </Box>

            <Typography fontSize={12} color={COLORS.textSecondary} sx={{ mb: 1 }}>
              {distribution === "roundrobin"
                ? totalSelected > 0
                  ? `Tick two or more — every Sales user is eligible, not just Sales Managers. The work splits evenly between them, in order. Capped at ${totalSelected} (one per lead) — more than that would leave someone with nothing.`
                  : "Tick two or more — every Sales user is eligible, not just Sales Managers. The work splits evenly between them, in order."
                : "Search for one recipient to receive all of it."}
            </Typography>

            {distribution === "single" ? (
              <Autocomplete
                options={recipientUsers
                  .filter((u) => u.userId !== sourceUser?.userId)
                  .filter((u) => u.isCrmUser !== true)}
                getOptionLabel={(u) => u.name || ""}
                isOptionEqualToValue={(o, v) => o.userId === v.userId}
                renderOption={(props, u) => (
                  <Box component="li" {...props} key={u.userId} sx={{ display: "block !important" }}>
                    <Typography fontSize={13.5} fontWeight={600}>
                      {u.name}
                      {u.roleId?.title && (
                        <Typography component="span" fontSize={11} color={COLORS.blue} sx={{ ml: 0.75, fontWeight: 600 }}>
                          {u.roleId.title}
                        </Typography>
                      )}
                    </Typography>
                    {reportsToLabel(u) && (
                      <Typography fontSize={12} color={COLORS.textSecondary}>{reportsToLabel(u)}</Typography>
                    )}
                  </Box>
                )}
                value={recipients[0] || null}
                onChange={(e, val) => setRecipients(val ? [val] : [])}
                renderInput={(params) => <TextField {...params} placeholder="Search for a recipient" />}
                sx={fieldSx}
              />
            ) : (
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 1,
                  maxHeight: 220,
                  overflowY: "auto",
                  p: 1.5,
                  border: `1px solid ${COLORS.border}`,
                  borderRadius: 2,
                  bgcolor: COLORS.sunken,
                }}
              >
                {recipientUsers
                  .filter((u) => u.userId !== sourceUser?.userId)
                  .filter((u) => u.isCrmUser !== true)
                  .map((u) => {
                    const isSelected = recipients.some((r) => r.userId === u.userId);
                    const count = recipientCounts.find((r) => r.userId === u.userId)?.count;
                    const isCapped = !isSelected && totalSelected > 0 && recipients.length >= totalSelected;
                    return (
                      <Box
                        key={u.userId}
                        onClick={isCapped ? undefined : () => toggleRecipient(u)}
                        title={isCapped ? `Capped at ${totalSelected} recipients — one per lead` : reportsToLabel(u) || undefined}
                        sx={{
                          px: 1.5,
                          py: 0.6,
                          borderRadius: 2,
                          border: `1px solid ${isSelected ? COLORS.blue : COLORS.border}`,
                          bgcolor: isSelected ? COLORS.blueLight : "#fff",
                          color: isSelected ? COLORS.blue : "#111827",
                          cursor: isCapped ? "not-allowed" : "pointer",
                          opacity: isCapped ? 0.45 : 1,
                          whiteSpace: "nowrap",
                          lineHeight: 1.3,
                        }}
                      >
                        <Typography component="div" fontSize={12.5} fontWeight={isSelected ? 700 : 600} color="inherit">
                          {u.name}
                          {isSelected && typeof count === "number" && ` — ${count}`}
                        </Typography>
                        {u.roleId?.title && (
                          <Typography component="div" fontSize={10.5} color={isSelected ? COLORS.blue : COLORS.textSecondary}>
                            {u.roleId.title}
                          </Typography>
                        )}
                      </Box>
                    );
                  })}
              </Box>
            )}

            <Typography fontSize={13} fontWeight={700} sx={{ mt: 3, mb: 1 }}>
              Summary
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, mb: 2 }}>
              {summaryLines.map((line, i) => (
                <Typography key={i} fontSize={13.5}>
                  • {line}
                </Typography>
              ))}
            </Box>

            <FieldLabel>
              Reason <span style={{ color: COLORS.red }}>*</span>
            </FieldLabel>
            <TextField
              required
              multiline
              minRows={2}
              fullWidth
              placeholder="e.g. Rahul resigned, effective 09 Aug 2026"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              error={reason.trim().length === 0}
              helperText={reason.trim().length === 0 ? "A reason is required before this transfer can be confirmed." : " "}
              sx={fieldSx}
            />

            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mt: 2 }}>
              <SecondaryButton onClick={() => setActiveStep(2)} disabled={confirming}>
                ← Back
              </SecondaryButton>
              <Box sx={{ textAlign: "right" }}>
                <PrimaryButton onClick={() => setConfirmDialogOpen(true)} disabled={confirmDisabled}>
                  {confirming ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Confirm transfer"}
                </PrimaryButton>
                {!canExecute && (
                  <Typography fontSize={12} color={COLORS.textSecondary} sx={{ mt: 1, maxWidth: 260 }}>
                    Your role has view-only access — you can browse this transfer, but can't confirm it.
                  </Typography>
                )}
                {canExecute && recipients.length === 0 && (
                  <Typography fontSize={12} color={COLORS.textSecondary} sx={{ mt: 1 }}>
                    Select a recipient above first.
                  </Typography>
                )}
                {canExecute && recipients.length > 0 && distribution === "roundrobin" && recipients.length < 2 && (
                  <Typography fontSize={12} color={COLORS.textSecondary} sx={{ mt: 1 }}>
                    Round robin needs at least 2 recipients.
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        )}

        <WorkTransferNotice notice={notice} onClose={() => setNotice(null)} />

        {/* Confirm transfer used to fire immediately on click — one misclick
            moved real leads/approvals with no way back short of another
            manual transfer. This is a final yes/no checkpoint reusing the
            same summaryLines already shown above it, so there's nothing new
            to read, just one more deliberate step (2026-08-25). */}
        <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>Confirm this transfer?</DialogTitle>
          <DialogContent>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, mb: 1 }}>
              {summaryLines.map((line, i) => (
                <Typography key={i} fontSize={13.5}>
                  • {line}
                </Typography>
              ))}
            </Box>
            <Typography fontSize={12.5} color={COLORS.textSecondary}>
              This cannot be undone automatically — moved leads and carried-forward approvals would need a separate manual transfer to reverse.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <SecondaryButton onClick={() => setConfirmDialogOpen(false)}>No, go back</SecondaryButton>
            <PrimaryButton
              onClick={() => {
                setConfirmDialogOpen(false);
                confirmTransfer();
              }}
            >
              Yes, confirm
            </PrimaryButton>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
}
